/**
 * Cart drawer gift-upsell checkbox (snippets/lc-cart-extras.liquid, part 'gift'). Checking adds one unit of the gift
 * product, unchecking removes its line — both through Shopify.actions.updateCart, whose shopify:cart:lines-update makes
 * Horizon's cart-items-component re-render the drawer section (hydration morph). The element is stateless: the server
 * render carries data-in-cart / data-line-key / the checked attribute, and updatedCallback re-syncs the box after a morph.
 * While a request is in flight the row is aria-busy and the box disabled; a failure reverts the box and announces the
 * error in the role=status span. The "Others also bought" cards are lc-product-card elements (assets/lc-product-card.js).
 */
import { Component } from '@theme/component';

class LcCartGift extends Component {
  requiredRefs = ['box'];

  updatedCallback() {
    super.updatedCallback();
    const box = this.refs.box;
    if (box instanceof HTMLInputElement && !box.disabled) box.checked = this.dataset.inCart === 'true';
  }

  async toggle() {
    const box = this.refs.box;
    const actions = window.Shopify?.actions;
    if (!(box instanceof HTMLInputElement)) return;
    const add = box.checked;
    const line = add
      ? { merchandiseId: `gid://shopify/ProductVariant/${this.dataset.variantId}`, quantity: 1 }
      : { id: this.dataset.lineKey, quantity: 0 };
    if (!actions?.updateCart || (!add && !line.id)) {
      box.checked = !add;
      return;
    }
    this.setAttribute('aria-busy', 'true');
    box.disabled = true;
    const before = this.dataset.inCart; // the re-render (hydration on cart:lines-update) rewrites data-in-cart with the server truth
    try {
      const result = await actions.updateCart({ lines: [line] }, { event: { context: 'cart' } });
      if (result?.userErrors?.length) throw new Error(result.userErrors.map((e) => e.message).join(', '));
      this.#announce(add ? this.dataset.textAdded : '');
    } catch (error) {
      console.error('[lc-cart-gift] update failed', error);
      if (this.dataset.inCart === before) box.checked = !add; // only revert when the cart really did not change
      this.#announce(this.dataset.textError);
    } finally {
      box.disabled = false;
      this.removeAttribute('aria-busy');
      // the server state wins once the morph has landed (the disabled guard in updatedCallback skipped it mid-flight)
      if (this.dataset.inCart !== before) box.checked = this.dataset.inCart === 'true';
    }
  }

  /** @param {string | undefined} text */
  #announce(text) {
    if (this.refs.status instanceof HTMLElement) this.refs.status.textContent = text ?? '';
  }
}

if (!customElements.get('lc-cart-gift')) {
  customElements.define('lc-cart-gift', LcCartGift);
}
